import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useNavigate } from 'react-router-dom';
import styles from './ContactForm.module.css';

// Input sanitization utility
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
    .slice(0, 1000); // Limit length
};

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^[+]?[\s\-()]*([0-9][\s\-()]*){10,}$/;
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate(); // Initialize useNavigate

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const sanitizedValue = type === 'checkbox' ? checked : sanitizeInput(value);
    setFormData({
      ...formData,
      [name]: sanitizedValue,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      alert('Please enter your name.');
      return;
    }

    if (!formData.message.trim()) {
      alert('Please enter a message.');
      return;
    }

    // Validate that either email or phone is provided
    const hasValidEmail = formData.email.trim() && validateEmail(formData.email);
    const hasValidPhone = formData.phone.trim() && validatePhone(formData.phone);
    
    if (!hasValidEmail && !hasValidPhone) {
      alert('Please provide either a valid email address or phone number so we can reach you.');
      return;
    }

    setIsSubmitting(true);

    const templateParams = {
      from_name: sanitizeInput(formData.name).trim(),
      from_email: sanitizeInput(formData.email).trim(),
      from_phone: sanitizeInput(formData.phone).trim(),
      message: sanitizeInput(formData.message).trim(),
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (response) => {
          console.log('Email sent successfully:', response);
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: '',
          });
          // Navigate to the Thank You page
          navigate('/thank-you');
        },
        (error) => {
          console.error('Failed to send email:', error);
          alert('Oops! Something went wrong. Please try again later.');
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {/* Form Fields */}
      <div className={styles.formGroup}>
        <label htmlFor="name" className={styles.label}>
          Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.label}>
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="phone" className={styles.label}>
          Phone
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="(480) 555-0123"
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="message" className={styles.label}>
          How can I help you?
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="4"
          placeholder="Tell me about your project or what you'd like to discuss..."
          className={styles.textarea}
          required
        />
      </div>

      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
};

export default ContactForm;
